/**
 * Presentation helpers for a `Determination`. Deliberately separate from
 * `determine()`: this module formats, it never decides. It lives in the engine
 * package because BOTH callers need identical output — the browser result page
 * and the emailed copy — and two implementations of the same sum would drift,
 * which is the exact failure the consent-wording parity test exists to catch.
 *
 * The engine's purity rules still hold here: no clock, no randomness, no I/O.
 * `Intl` is none of those.
 */
import type { RequirementResult } from "./types.js";

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

export interface WorkingStep {
  /** The sum, written out. */
  expression: string;
  /** What the sum produced. */
  result: string;
  /** Optional plain-English gloss of the step. */
  note?: string;
}

/**
 * The arithmetic behind a financial requirement, written out.
 *
 * Every competitor tool checked shows a verdict and hides the sum, which
 * leaves the reader unable to see WHY the number is what it is — or to spot
 * that the tool used the wrong multiplier. Showing the working is the point.
 */
export function financialWorking(r: RequirementResult): WorkingStep[] {
  const f = r.figures;
  if (!f) return [];

  const threshold = f["threshold"];
  const income = f["income"];
  const floor = f["floor"];
  const multiplier = f["multiplier"];
  const shortfall = f["shortfall"];
  const required = f["required_savings"];
  const held = f["savings_held"];
  const headroom = f["headroom"];

  const steps: WorkingStep[] = [];

  // Income-only: salary against the threshold.
  if (income !== undefined && required === undefined && threshold !== undefined) {
    steps.push({
      expression: `${GBP.format(income)} salary − ${GBP.format(threshold)} required`,
      result:
        headroom === 0
          ? "exactly the figure, with nothing spare"
          : headroom !== undefined && headroom > 0
            ? `${GBP.format(headroom)} above the figure`
            : `${GBP.format(Math.abs(headroom ?? 0))} below the figure`,
    });
    return steps;
  }

  // Savings, alone or combined with income.
  if (required !== undefined && floor !== undefined && multiplier !== undefined) {
    if (income !== undefined && income > 0 && shortfall !== undefined && threshold !== undefined) {
      steps.push({
        expression: `${GBP.format(threshold)} required − ${GBP.format(income)} salary`,
        result: `${GBP.format(shortfall)} shortfall`,
        note: "Savings have to cover the part the salary does not.",
      });
    }
    const shortfallUsed = shortfall ?? threshold ?? 0;
    steps.push({
      // At settlement the multiplier is 1, and writing "1 ×" reads as though
      // an operation happened where none did.
      expression:
        multiplier === 1
          ? `${GBP.format(floor)} + ${GBP.format(shortfallUsed)}`
          : `${GBP.format(floor)} + (${multiplier} × ${GBP.format(shortfallUsed)})`,
      result: `${GBP.format(required)} of savings`,
      note:
        multiplier === 1
          ? "At settlement the whole amount above the £16,000 floor counts, so the shortfall is not multiplied."
          : `Before settlement the shortfall is multiplied by ${multiplier}, because the savings must cover the period until the next application.`,
    });
    if (held !== undefined && headroom !== undefined) {
      steps.push({
        expression: `${GBP.format(held)} held − ${GBP.format(required)} required`,
        // Landing exactly on the figure is common and worth saying plainly;
        // "£0 more than the figure" reads as though something went wrong.
        result:
          headroom === 0
            ? "exactly the figure, with nothing spare"
            : headroom > 0
              ? `${GBP.format(headroom)} more than the figure`
              : `${GBP.format(-headroom)} short of the figure`,
      });
    }
  }
  return steps;
}

/**
 * How savings count on the savings route, for a given stage.
 *
 * The floor and multiplier are passed in rather than written here: they are
 * figures with rows in the register, and the ruleset is the only place allowed
 * to hold their values (ADR-0011). A caller that hardcoded them would be a
 * second source of truth that a rules change would not reach.
 */
export function savingsRuleForStage(stage: string, floor: number, multiplier: number): string {
  const f = GBP.format(floor);
  return stage === "ILR"
    ? `savings above ${f} count in full`
    : `savings above ${f} count at ${multiplier}× — divided by ${multiplier}`;
}

/**
 * The things this check does not look at.
 *
 * Stated in one place because the page and the emailed copy must say the same
 * thing: a limitation the email omits is a limitation the reader never sees,
 * and the email is the copy they keep. Prose, not rule ids — these are the
 * gaps BETWEEN the ruleset and a real application, so there is nothing in the
 * ruleset to derive them from.
 */
export const CHECK_LIMITS: readonly string[] = [
  "Suitability — a criminal record, a previous refusal, overstaying, or anything said in an earlier application. Where those apply, this route is not the right tool and we say so rather than scoring it.",
  "Whether the relationship is genuine, whether you intend to live together permanently, and whether the accommodation is adequate. A caseworker judges those on your evidence; a calculation cannot.",
  "Your documents — whether they are the right ones, in the right format, dated inside their window, complete, and translated where they need to be.",
  "The rest of a real application: the TB certificate where your country needs one, biometrics, the application fee and the immigration health surcharge.",
  "Anything that turns on the exact date you apply. Figures and levels change, and the ones used here are the ones in force today.",
];
